import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useState } from 'react';
import { getAssetTypeName, useOpenAsset } from '../../../data/assets/helpers';
import { OrganizationViewStateMetadata } from '../../../api/models/apis';
import { useApiContext } from '../../../api';
import { formatDate } from '../../../components/miscUtils';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import { promptAutoForm } from '../../../components/auto-form-modal/AutoForm';
import { promptConfirmationBox } from '../../../components/message-boxes/ConfirmBox';
import { editViewStateForm } from '../../visual-analytics/dashboard/components/editViewStateForm';
import { getAssetTypeIcon } from '../../../data/assets/helpers';
import styles from './OrganizationViewStates.module.scss';
import { selectTemplatesAndCombinedOntology } from '../../../data/templateSlice';
import { useAppSelector, useUser } from '../../../app/hooks';
import { VISIBILITY_CONFIG } from '../../visual-analytics/dashboard/constants/visibility-config';
import { VIEW_STATE_TERMS } from '../../../data/snapshot/dashboardStateSerialization';

interface ViewStateCardProps {
  viewState: OrganizationViewStateMetadata;
  onChanged: () => Promise<void>;
}

function ActionButton({
  icon,
  title,
  onClick,
  isDisabled,
}: {
  icon: IconProp;
  title: string;
  onClick: () => void;
  isDisabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={styles.cardIconButton}
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={isDisabled ?? false}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  );
}

export function ViewStateCard({ viewState, onChanged }: ViewStateCardProps) {
  const api = useApiContext();
  const { user } = useUser();
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  const openAsset = useOpenAsset();
  const [isCopied, setIsCopied] = useState(false);

  const stateUrl = `/asset/${viewState.assetId}?viewState=${encodeURIComponent(viewState.id)}`;
  const assetTypeOntology = viewState.assetType ? ontology.assetTypes[viewState.assetType] : null;
  const assetTypeName = assetTypeOntology ? getAssetTypeName(assetTypeOntology) : 'Unknown';
  const assetTypeIcon = assetTypeOntology ? getAssetTypeIcon(assetTypeOntology) : null;
  const visibilityConfig = VISIBILITY_CONFIG[viewState.visibility as keyof typeof VISIBILITY_CONFIG];
  const visibilityIcon = visibilityConfig?.icon || ['fas', 'question-circle'];
  const hasEditPermission = user?.isOrganizationAdmin;
  const permissionDeniedMessage = 'Only organization admins can perform this action.';

  async function editState() {
    try {
      await promptAutoForm({
        ...editViewStateForm({ viewState, updateViewState: api.viewState.updateViewState }),
      });
      await onChanged();
    } catch {
      // Cancelled forms do not need an error message.
    }
  }

  async function deleteState() {
    try {
      await promptConfirmationBox({
        title: `Delete saved ${VIEW_STATE_TERMS.singular}?`,
        description: `This will permanently delete the ${VIEW_STATE_TERMS.singular} <strong>${viewState.name}</strong>.`,
        warningAlert: true,
      });
      await api.viewState.deleteViewState(viewState.id);
      await onChanged();
    } catch (error) {
      if (error) showErrorMessage(error instanceof Error ? error.message : String(error));
    }
  }

  async function copyLink() {
    const permalink = `${window.location.origin}${stateUrl}`;
    try {
      setIsCopied(true);
      await navigator.clipboard.writeText(permalink);
    } catch {
      showErrorMessage(`Unable to copy link to clipboard. Copy link manually: ${permalink}`);
    } finally {
      setTimeout(() => setIsCopied(false), 2000);
    }
  }

  // Hover title for the card
  const cardHoverTitle = [
    viewState.name,
    viewState.description || '',
    `Asset: ${viewState.assetDisplayName || 'Unknown'}`,
    `Asset type: ${assetTypeName}`,
    `Created by: ${viewState.createdBy || 'Unknown'}`,
    `Updated at: ${formatDate(viewState.updatedAt)}`,
    `Visibility: ${visibilityConfig.label}`,
  ].join('\n');

  return (
    <article className={styles.viewStateCard} title={cardHoverTitle}>
      <div className={styles.cardHeader}>
        <div className={styles.assetIcon}>
          <FontAwesomeIcon icon={assetTypeIcon as IconProp} />
        </div>
        <div className={styles.cardTitle}>{viewState.assetDisplayName || 'Unknown asset'}</div>
        <div className={styles.cardActions}>
          <ActionButton
            icon={['fas', 'folder-open']}
            title={`Open ${VIEW_STATE_TERMS.singular}`}
            onClick={() => openAsset(viewState.assetId, viewState.id)}
          />
          <ActionButton
            icon={isCopied ? ['fas', 'clipboard-check'] : ['fas', 'clipboard']}
            title={isCopied ? 'Link copied!' : 'Copy link'}
            onClick={copyLink}
          />
          <ActionButton
            icon={['fas', 'pencil']}
            title={!hasEditPermission ? permissionDeniedMessage : 'Edit'}
            onClick={editState}
            isDisabled={!hasEditPermission}
          />
          <ActionButton
            icon={['fas', 'trash-can']}
            title={!hasEditPermission ? permissionDeniedMessage : 'Delete'}
            onClick={deleteState}
            isDisabled={!hasEditPermission}
          />
        </div>
      </div>
      <div className={styles.cardNameBig}>{viewState.name}</div>
      <p className={styles.cardDescription}>{viewState.description}</p>
      <div className={styles.cardFooter}>
        <div className={styles.cardCreatedBy}>
          <FontAwesomeIcon className={styles.viewStateVisibilityIcon} icon={visibilityIcon} />
          {viewState.createdBy || 'Unknown'}
        </div>
        <time className={styles.cardDate} dateTime={viewState.updatedAt}>
          {formatDate(viewState.updatedAt)}
        </time>
      </div>
    </article>
  );
}
